/**
 * ViewportInspector — property panel for a selected VIEWPORT entity.
 *
 * Shown in the inspector sidebar when exactly one Viewport entity is
 * selected and the active space is a paper-space layout (PSPACE mode).
 *
 * AutoCAD parity:
 *   - Scale: preset dropdown (standard ratios) + custom freeform inputs.
 *   - Lock: checkbox toggling displayLocked (MVIEW LOCK / MVIEW UNLOCK).
 *   - Shade plot: dropdown for the plot-time shade mode (DXF group 170 —
 *     As Displayed / Wireframe / Hidden / Rendered). PLOT-TIME-ONLY: no
 *     on-screen render effect; modes 4 Visual Style / 5 Render Preset are a
 *     documented deferred scope (need the 348/333 handle subsystem).
 *   - Layers tab: per-VP frozen layer list (VPLAYER F / T).
 *   - UCS per VP: read-only display if present (MC5 scope).
 *   - Clip mode: read-only, reflecting the SOURCE-preserved clip kind —
 *     "Polygonal" when the viewport carries a genuinely non-rectangular clip,
 *     signalled by ANY of: the authored/native paper boundary
 *     `clipBoundaryPaperSpace`, the DXF group-90 non-rect bit
 *     `statusBitFlags & 0x10000`, OR the SPATIAL_FILTER boundary `clipBoundaryKind`
 *     (the imported boundary's point-count kind, covering a polygon clip whose
 *     group-90 bit is 0), else "Rectangular". A 2-point rectangular
 *     SPATIAL_FILTER import (expanded to a 4-corner `clipPath`) reads
 *     Rectangular — the resolved `clipPath` is not inferred from (@codex #496).
 *
 * All mutations go through dispatch (geometry/viewport-* commands) —
 * the component never writes entity fields directly.
 */

import * as React from "react";
import type { ViewportEntity } from "@dxf-ai-editor/drawing-model";
import { readViewportOverrides } from "@dxf-ai-editor/drawing-model";
import { VIEWPORT_NONRECT_CLIP_BIT } from "@dxf-ai-editor/command-engine";
import { useEditorStore } from "../state/editorStore";
import { useT } from "../i18n";
import {
  SHADE_PLOT_MODES,
  shadePlotModeLabel,
} from "../state/viewportShadePlot";

// ---------------------------------------------------------------------------
// Preset scale options (AutoCAD standard scale list)
// ---------------------------------------------------------------------------

interface ScalePreset {
  label: string;
  num: number;
  denom: number;
}

const SCALE_PRESETS: ScalePreset[] = [
  { label: "1:1",   num: 1, denom: 1 },
  { label: "1:2",   num: 1, denom: 2 },
  { label: "1:5",   num: 1, denom: 5 },
  { label: "1:10",  num: 1, denom: 10 },
  { label: "1:20",  num: 1, denom: 20 },
  { label: "1:50",  num: 1, denom: 50 },
  { label: "1:100", num: 1, denom: 100 },
  { label: "1:200", num: 1, denom: 200 },
  { label: "1:500", num: 1, denom: 500 },
];

const CUSTOM_SCALE_VALUE = "custom";

function scaleToPresetValue(num: number, denom: number): string {
  const match = SCALE_PRESETS.find(
    (p) => p.num === num && p.denom === denom,
  );
  return match ? match.label : CUSTOM_SCALE_VALUE;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ViewportInspectorProps {
  /** The selected viewport entity. */
  viewport: ViewportEntity;
  /** All layer names in the current drawing document. */
  allLayerNames: string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ViewportInspector({
  viewport,
  allLayerNames,
}: ViewportInspectorProps): React.ReactElement {
  const t = useT();
  const dispatch = useEditorStore((store) => store.dispatch);

  // Read in-memory overrides (XDATA or entity fields)
  const overrides = readViewportOverrides(viewport);
  const { frozenLayers, scale, locked, ucsName } = overrides;

  // Effective lock: entity.displayLocked OR XDATA override.locked, consistent
  // with frame rendering + dblclick gating which use the same OR semantics
  // (iter-5 P2 fix #3 — inspector was using only overrides.locked).
  const effectiveLocked = (viewport.displayLocked ?? false) || locked;

  // ── Scale state ───────────────────────────────────────────────────────────
  const presetValue = scaleToPresetValue(scale.num, scale.denom);

  // Explicit flag so "Custom…" selection is sticky until the user picks a
  // preset or the underlying scale changes back to a known preset (e.g. undo).
  const [isCustomScale, setIsCustomScale] = React.useState(
    presetValue === CUSTOM_SCALE_VALUE,
  );

  const [customNum, setCustomNum] = React.useState(String(scale.num));
  const [customDenom, setCustomDenom] = React.useState(String(scale.denom));

  // Keep custom inputs in sync when the underlying VP changes (e.g. undo).
  // If the new scale resolves to a known preset, exit custom mode.
  React.useEffect(() => {
    setCustomNum(String(scale.num));
    setCustomDenom(String(scale.denom));
    setIsCustomScale(scaleToPresetValue(scale.num, scale.denom) === CUSTOM_SCALE_VALUE);
  }, [scale.num, scale.denom]);

  // The value shown in the <select>: preset label when a known preset is active,
  // or CUSTOM_SCALE_VALUE when the user has explicitly chosen "Custom…".
  const selectDisplayValue = isCustomScale ? CUSTOM_SCALE_VALUE : presetValue;

  function handleScalePresetChange(
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void {
    const val = e.target.value;
    if (val === CUSTOM_SCALE_VALUE) {
      // Reveal freeform inputs; do NOT dispatch yet — wait for commit.
      setIsCustomScale(true);
      return;
    }
    const preset = SCALE_PRESETS.find((p) => p.label === val);
    if (!preset) return;
    setIsCustomScale(false);
    dispatch({
      type: "geometry/viewport-set-scale",
      source: "scene",
      viewportId: viewport.id,
      scaleNum: preset.num,
      scaleDenom: preset.denom,
    });
  }

  function commitCustomScale(): void {
    const num = parseFloat(customNum);
    const denom = parseFloat(customDenom);
    if (Number.isFinite(num) && Number.isFinite(denom) && num > 0 && denom > 0) {
      dispatch({
        type: "geometry/viewport-set-scale",
        source: "scene",
        viewportId: viewport.id,
        scaleNum: num,
        scaleDenom: denom,
      });
    }
  }

  // ── Lock toggle ───────────────────────────────────────────────────────────

  function handleLockChange(e: React.ChangeEvent<HTMLInputElement>): void {
    dispatch({
      type: "geometry/viewport-set-lock",
      source: "scene",
      viewportId: viewport.id,
      locked: e.target.checked,
    });
  }

  // ── Shade-plot mode (DXF 170 — plot-time only, no on-screen render) ────────
  // shadePlotMode is a first-class entity field; default 0 ("As Displayed")
  // when unset. A stored value outside the supported authoring range 0..3 (e.g.
  // an imported file's mode 4 Visual Style / 5 Render Preset — deferred scope)
  // is shown verbatim via an extra disabled "Other (N)" option rather than
  // being silently coerced to a different mode (which a no-change re-select
  // would otherwise corrupt).
  const shadePlotMode = viewport.shadePlotMode ?? 0;
  const shadePlotOutOfRange = !SHADE_PLOT_MODES.includes(
    shadePlotMode as (typeof SHADE_PLOT_MODES)[number],
  );

  function handleShadePlotChange(
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void {
    const next = Number(e.target.value);
    // Guard: only the supported 0..3 values are authorable (the "Other" option
    // is disabled, so this should never fire for it, but defend anyway).
    if (
      !SHADE_PLOT_MODES.includes(next as (typeof SHADE_PLOT_MODES)[number])
    ) {
      return;
    }
    dispatch({
      type: "geometry/viewport-set-shadeplot",
      source: "scene",
      viewportId: viewport.id,
      shadePlotMode: next,
    });
  }

  // ── Layer freeze / thaw ───────────────────────────────────────────────────

  const frozenSet = new Set(frozenLayers);

  function handleLayerFreezeToggle(layerName: string): void {
    const isFrozen = frozenSet.has(layerName);
    dispatch({
      type: "geometry/viewport-vplayer",
      source: "scene",
      viewportId: viewport.id,
      action: isFrozen ? "thaw" : "freeze",
      layerName,
    });
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className="section-subcard"
      data-testid="viewport-inspector"
    >
      {/* Header */}
      <div className="section-heading">
        <div>
          <p className="section-label">{t("vp.inspector.title")}</p>
          <h3>{viewport.id}</h3>
        </div>
      </div>

      <div className="panel-stack">
        {/* ── Scale section ──────────────────────────────────────────── */}
        <div className="property-editor-row" data-testid="vp-scale-row">
          <span>{t("vp.inspector.scale")}</span>
          <select
            className="property-input"
            data-testid="vp-scale-select"
            value={selectDisplayValue}
            onChange={handleScalePresetChange}
          >
            {SCALE_PRESETS.map((p) => (
              <option key={p.label} value={p.label}>
                {p.label}
              </option>
            ))}
            <option value={CUSTOM_SCALE_VALUE}>
              {t("vp.inspector.scale.custom")}
            </option>
          </select>
        </div>

        {/* Custom scale freeform inputs — shown only for non-preset scales */}
        {isCustomScale ? (
          <div
            className="two-column-grid"
            data-testid="vp-scale-custom-inputs"
          >
            <label className="field-stack">
              <span>{t("vp.inspector.scale.num")}</span>
              <input
                className="property-input"
                type="number"
                min="1"
                data-testid="vp-scale-num-input"
                value={customNum}
                onChange={(e) => setCustomNum(e.target.value)}
                onBlur={commitCustomScale}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitCustomScale();
                }}
              />
            </label>
            <label className="field-stack">
              <span>{t("vp.inspector.scale.denom")}</span>
              <input
                className="property-input"
                type="number"
                min="1"
                data-testid="vp-scale-denom-input"
                value={customDenom}
                onChange={(e) => setCustomDenom(e.target.value)}
                onBlur={commitCustomScale}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitCustomScale();
                }}
              />
            </label>
          </div>
        ) : null}

        {/* ── Lock ──────────────────────────────────────────────────── */}
        <label
          className="property-editor-row"
          data-testid="vp-lock-row"
        >
          <span>{t("vp.inspector.lock")}</span>
          <input
            type="checkbox"
            data-testid="vp-lock-checkbox"
            checked={effectiveLocked}
            onChange={handleLockChange}
          />
        </label>

        {/* ── Shade plot (DXF 170 — plot-time only) ─────────────────── */}
        <div className="property-editor-row" data-testid="vp-shadeplot-row">
          <span>{t("vp.inspector.shadeplot")}</span>
          {/* When the viewport is CURRENTLY in an out-of-range mode (imported
              Visual Style 4 / Render Preset 5), the whole control is rendered
              read-only (disabled) — mirroring the by-construction mutation
              guard in applyViewportSetShadePlot, which no-ops on a 4/5 viewport.
              Editing those modes needs the deferred 348/333 visual-style
              subsystem; until then the user cannot change such a viewport's plot
              mode. The disabled "Other (N)" option below still shows the stored
              value verbatim as the selection. */}
          <select
            className="property-input"
            data-testid="vp-shadeplot-select"
            value={String(shadePlotMode)}
            onChange={handleShadePlotChange}
            disabled={shadePlotOutOfRange}
          >
            {SHADE_PLOT_MODES.map((mode) => (
              <option key={mode} value={String(mode)}>
                {shadePlotModeLabel(mode, t)}
              </option>
            ))}
            {/* Out-of-range stored value (e.g. imported mode 4/5): show it
                verbatim as a disabled option so a no-change render does not
                corrupt it, and selecting it is impossible. */}
            {shadePlotOutOfRange ? (
              <option
                value={String(shadePlotMode)}
                disabled
                data-testid="vp-shadeplot-other-option"
              >
                {shadePlotModeLabel(shadePlotMode, t)}
              </option>
            ) : null}
          </select>
        </div>

        {/* ── UCS (read-only) ────────────────────────────────────────── */}
        {ucsName !== undefined ? (
          <div className="property-editor-row" data-testid="vp-ucs-row">
            <span>{t("vp.inspector.ucs")}</span>
            <span data-testid="vp-ucs-value">{ucsName}</span>
          </div>
        ) : null}

        {/* ── Clip mode (read-only) — reflects the SOURCE-preserved clip boundary
             KIND, not a geometry inference. "Polygonal" ⟺ the viewport carries a
             genuinely NON-RECTANGULAR clip, signalled by ANY of three
             source-preserved fields:
               (a) the authored / native paper-space boundary polygon
                   (`clipBoundaryPaperSpace`, set by VPCLIP
                   `applyViewportClipSetPolygon` + native group-340 clips);
               (b) the DXF-canonical group-90 non-rectangular-clip bit
                   (`statusBitFlags & 0x10000`), preserved verbatim from an
                   imported SPATIAL_FILTER polygon clip that sets it;
               (c) the SPATIAL_FILTER boundary KIND (`clipBoundaryKind`), captured
                   from the imported boundary's POINT COUNT at resolve time —
                   this covers a polygon SPATIAL_FILTER whose group-90 non-rect
                   bit is 0 (some producers omit it), which carries neither (a)
                   nor (b) and would otherwise mislabel as Rectangular (@codex
                   #496 P3).
             The resolved model-space `clipPath` is deliberately NOT consulted:
             the SPATIAL_FILTER resolver expands a documented TWO-point
             RECTANGULAR boundary into a 4-corner `clipPath`, so `clipPath.length
             >= 3` would mislabel that rectangular clip as Polygonal — the reason
             the KIND is preserved at parse time instead. Every signal used here
             is set by the source / the authoring command — never inferred from
             clip geometry — so a 2-point rectangular import (no boundary polygon,
             no non-rect bit, `clipBoundaryKind === "rectangular"`) correctly
             reads Rectangular. ── */}
        <div className="property-editor-row" data-testid="vp-clip-row">
          <span>{t("vp.inspector.clip")}</span>
          <span data-testid="vp-clip-value">
            {(viewport.clipBoundaryPaperSpace &&
              viewport.clipBoundaryPaperSpace.length >= 3) ||
            ((viewport.statusBitFlags ?? 0) & VIEWPORT_NONRECT_CLIP_BIT) !== 0 ||
            viewport.clipBoundaryKind === "polygonal"
              ? t("vp.inspector.clip.polygonal")
              : t("vp.inspector.clip.rect")}
          </span>
        </div>

        {/* ── Layers tab — frozen layers list ──────────────────────── */}
        <div data-testid="vp-layers-section">
          <p className="section-label" style={{ marginBottom: 4 }}>
            {t("vp.inspector.layers")}
          </p>
          <div
            className="property-grid"
            data-testid="vp-layers-grid"
          >
            {allLayerNames.map((layerName) => {
              const isFrozen = frozenSet.has(layerName);
              return (
                <label
                  key={layerName}
                  className="property-editor-row"
                  data-testid={`vp-layer-row-${layerName}`}
                >
                  <span>{layerName}</span>
                  <input
                    type="checkbox"
                    data-testid={`vp-layer-freeze-${layerName}`}
                    aria-label={`${t("vp.inspector.layers.frozen")}: ${layerName}`}
                    checked={isFrozen}
                    onChange={() => handleLayerFreezeToggle(layerName)}
                  />
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
